/**
 * REGISTERING INTEREST (migration 0040) — a public page where a person says they would like to use Leveri, and what
 * they would like it to do, before they have an account. Invitations are sent from the list in waves.
 *
 * The choices are written in an accountant's own words, from what accountants asked for when invited to say what
 * they want to automate. They are what a person WANTS, never a claim about what Leveri does.
 */
import { z } from "zod";
export const INTEREST_WANTS = [
    { key: "BANK_RECONCILIATION", label: "Bank reconciliation" },
    { key: "BANK_CATEGORISATION", label: "Sorting bank statements into accounts" },
    { key: "ACCOUNTS_FROM_RECORDS", label: "Financial statements from bank statements or incomplete records" },
    { key: "TRIAL_BALANCE", label: "A trial balance from the ledger" },
    { key: "MANAGEMENT_ACCOUNTS", label: "Management accounts and reports" },
    { key: "ACCOUNT_RECONCILIATIONS", label: "Supplier, customer and other account reconciliations" },
    { key: "AUDIT", label: "Audit work: vouching, testing, workpapers" },
    { key: "TAX", label: "Tax computations" },
    { key: "PAYROLL", label: "Payroll" },
    { key: "DATA_ANALYSIS", label: "Analysing large volumes of transactions" },
];
export const INTEREST_ROLES = [
    { key: "PARTNER", label: "Partner or owner" },
    { key: "MANAGER", label: "Manager" },
    { key: "ACCOUNTANT", label: "Accountant" },
    { key: "AUDITOR", label: "Auditor" },
    { key: "FINANCE_TEAM", label: "In a company's finance team" },
    { key: "STUDENT", label: "Student" },
    { key: "OTHER", label: "Something else" },
];
export const INTEREST_FIRM_SIZES = [
    { key: "SOLO", label: "Just me" },
    { key: "2_10", label: "2–10 people" },
    { key: "11_50", label: "11–50" },
    { key: "51_200", label: "51–200" },
    { key: "200_PLUS", label: "More than 200" },
];
/** An event or campaign a registration came from, as it appears in the page's address (`?from=ican-2026`). */
export const INTEREST_SOURCE = /^[a-z0-9][a-z0-9-]{0,39}$/;
const keysOf = (list) => list.map((x) => x.key);
/** An optional text field: blank means not given. */
const optionalText = (max) => z.string().trim().max(max).optional().transform((v) => (v ? v : undefined));
export const RegisterInterestRequest = z.object({
    source: z.string().trim().toLowerCase().regex(INTEREST_SOURCE).optional(),
    fullName: z.string().trim().min(1, "Say your name.").max(200),
    email: z.string().trim().toLowerCase().email("Give an email address we can reach you on.").max(254),
    phone: optionalText(40),
    firmName: optionalText(200),
    role: z.enum(keysOf(INTEREST_ROLES)).optional(),
    firmSize: z.enum(keysOf(INTEREST_FIRM_SIZES)).optional(),
    wants: z.array(z.enum(keysOf(INTEREST_WANTS))).max(20).default([]),
    wantsOther: optionalText(1000),
    consent: z.literal(true, { errorMap: () => ({ message: "Say that we may contact you about Leveri." }) }),
    /** A field people never see. A form that fills it in is not a person. */
    website: z.string().max(200).optional(),
});
export const RegisterInterestResponse = z.object({ registered: z.literal(true) });
//# sourceMappingURL=interest.js.map